import React from "react";
import {
  Box,
  Typography,
  Divider,
  Chip,
} from "@mui/material";
import type { BossBabyChatMessageDto } from "@/apis/bossBabyChatLog";

interface ChatMessagesTabProps {
  messages: BossBabyChatMessageDto[];
}

/**
 * Boss Baby 채팅 메시지 탭 컴포넌트
 *
 * @description Boss Baby 채팅 메시지를 채팅 형태로 표시하는 탭
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-29
 */
export const ChatMessagesTab: React.FC<ChatMessagesTabProps> = ({
  messages,
}) => {
  const getMessageTypeLabel = (sender: string) => {
    switch (sender) {
      case "user":
        return "User";
      case "ai":
        return "AI";
      default:
        return sender.toUpperCase();
    }
  };

  const getMessageBackgroundColor = (sender: string) => {
    switch (sender) {
      case "user":
        return "primary.main";
      case "ai":
        return "grey.100";
      default:
        return "grey.100";
    }
  };

  const getMessageTextColor = (sender: string) => {
    return sender === "user" ? "white" : "text.primary";
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Chat Messages ({messages.length})
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {messages.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
            backgroundColor: "grey.50",
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No messages found.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            maxHeight: "600px",
            overflowY: "auto",
            p: 1,
            backgroundColor: "grey.50",
            borderRadius: 2,
          }}
        >
          {messages.map((message) => (
            <Box
              key={message.messageId}
              sx={{
                display: "flex",
                justifyContent:
                  message.sender === "user" ? "flex-end" : "flex-start",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  maxWidth: "70%",
                  minWidth: "200px",
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: getMessageBackgroundColor(message.sender),
                  color: getMessageTextColor(message.sender),
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                {/* 메시지 헤더 */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                    gap: 1,
                  }}
                >
                  <Chip
                    label={getMessageTypeLabel(message.sender)}
                    size="small"
                    variant={message.sender === "user" ? "filled" : "outlined"}
                    sx={{
                      backgroundColor:
                        message.sender === "user"
                          ? "rgba(255,255,255,0.2)"
                          : undefined,
                      color:
                        message.sender === "user" ? "white" : undefined,
                      borderColor:
                        message.sender === "user"
                          ? "rgba(255,255,255,0.5)"
                          : undefined,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        message.sender === "user"
                          ? "rgba(255,255,255,0.8)"
                          : "text.secondary",
                      fontSize: "0.75rem",
                    }}
                  >
                    {new Date(message.regDttm).toLocaleString("en-US")}
                  </Typography>
                </Box>

                {/* 메시지 내용 */}
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    lineHeight: 1.5,
                  }}
                >
                  {message.content}
                </Typography>

                {/* 추가 정보 (AI 메시지인 경우) */}
                {message.sender === "ai" && (
                  <Box
                    sx={{
                      mt: 1,
                      pt: 1,
                      borderTop: "1px solid rgba(0,0,0,0.1)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <Chip
                        label={`Type: ${message.messageType}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.7rem", height: 20 }}
                      />
                      {message.result && (
                        <Chip
                          label="Has Result"
                          size="small"
                          variant="outlined"
                          color="success"
                          sx={{ fontSize: "0.7rem", height: 20 }}
                        />
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};